import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BackendService } from '../../../api/bff/backend.service';
import { FeatureType, Feedback } from '@shared/interfaces/feedback';
import { CardComponent } from '../../../components/card/card.component';
import { Observable, of, take } from 'rxjs';
import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import { FeedbackFilterComponent } from './feedback-filter/feedback-filter.component';
import { FeedbackDownloadComponent } from './feedback-download/feedback-download.component';
import { RatingIconComponent } from '../../../components/rating-icon/rating-icon.component';
import { mapAnswerId, mapFeedbackId } from './idMappers';
import type { ColDef } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';

interface FeedbackData {
  [x: string]: string | number | boolean | string[];
}
@Component({
  selector: 'app-feedback-page',
  imports: [
    CardComponent,
    AsyncPipe,
    AgGridAngular,
    DecimalPipe,
    FeedbackFilterComponent,
    FeedbackDownloadComponent,
    RatingIconComponent,
  ],
  providers: [DatePipe],
  templateUrl: './feedback-page.component.html',
  styleUrl: './feedback-page.component.css',
})
export class FeedbackPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private backend = inject(BackendService);
  private datePipe = inject(DatePipe);

  protected feedbackPromise: Observable<Feedback[]> = of([]);
  protected rowData = signal<FeedbackData[]>([]);
  protected colDefs = signal<ColDef[]>([]);
  protected defaultColDef: ColDef = {
    autoHeaderHeight: true,
    autoHeight: true,
    cellStyle: { lineHeight: '1.05', padding: '0.5rem 0' },
    flex: 1,
    minWidth: 160,
    resizable: true,
    wrapHeaderText: true,
    wrapText: true,
  };
  protected rating = signal(0);
  protected feature!: FeatureType;

  ngOnInit() {
    this.feature = (this.route.snapshot.paramMap.get('feature') || 'general') as FeatureType;
    this.feedbackPromise = this.backend.getFeedbackByFeature(this.feature);
    this.feedbackPromise.pipe(take(1)).subscribe((feedback) => {
      const rowData = feedback.map((item) => {
        const data: FeedbackData = {
          userEmail: [item.user.email, JSON.stringify(item.user)],
          createdAt: item.createdAt!,
          consent: item.consent.hasConsented,
          context: item.context,
          rating: item.rating,
        };
        item.feedback.forEach((feedbackItem) => {
          data[feedbackItem.questionId] = feedbackItem.answer;
        });
        return data;
      });

      const colDefs: ColDef[] = [
        {
          field: 'userEmail',
          headerName: $localize`User`,
          tooltipValueGetter: ({ value }) => value[1] ?? '',
          valueFormatter: ({ value }) => value[0] ?? '',
        },
        {
          field: 'createdAt',
          headerName: $localize`Date`,
          valueFormatter: ({ value }) => this.datePipe.transform(value, 'dd/MM/yyyy HH:mm') || '',
        },
        { field: 'consent', headerName: $localize`Consent` },
        {
          field: 'context',
          headerName: $localize`Context`,
          valueFormatter: ({ value }) =>
            String(value ?? '')
              .split('-')
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' '),
        },
      ];
      if (feedback.length && feedback[0].rating >= 0) {
        colDefs.push({ field: 'rating', headerName: $localize`Rating` });
      }

      const uniqueQuestions = new Set<string>();
      feedback.forEach((item) => item.feedback.forEach((feedbackItem) => uniqueQuestions.add(feedbackItem.questionId)));
      uniqueQuestions.forEach((questionId) => {
        colDefs.push({
          field: questionId,
          headerName: mapFeedbackId(this.feature, questionId),
          valueFormatter: ({ value }) => {
            return mapAnswerId(this.feature, questionId, Array.isArray(value) ? value : []).join(' ,');
          },
        });
      });

      this.rowData.set(rowData);
      this.colDefs.set(colDefs);
      this.rating.set(feedback.reduce((acc, item) => acc + item.rating, 0) / feedback.length);
    });
  }

  protected updateColumns({ visible, field }: { visible: boolean; field: string }) {
    this.colDefs.update((columns) =>
      columns.map((column) => (column.field === field ? { ...column, hide: !visible } : column)),
    );
  }
}
